import React, { useState, lazy, memo } from 'react';
import { Home, Users, Globe, Briefcase, Leaf, Zap, BarChart2, Search, ChevronDown, TrendingUp, AlertCircle, CheckCircle, Info, ArrowRight, PieChart, BarChart, Award, Target, ThumbsUp, ChevronRight, Plus, X, ChevronUp, Trophy, Star, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
export function ImpactPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('projects');
  const [searchQuery, setSearchQuery] = useState('');
  const [showOpportunityModal, setShowOpportunityModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  // User projects (simulated)
  const userProjects = [{
    id: 101,
    title: 'App for reducing food waste',
    description: 'Connects restaurants and supermarkets with consumers to reduce food waste.',
    progress: 65,
    startDate: '15/03/2023',
    endDate: '30/08/2023',
    team: [{
      name: 'Sophia Rossi',
      role: 'UX Designer',
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/woFngADa1aU2Q5MqNXmffj/pexels-olly-774909.jpg'
    }, {
      name: 'Marco Bianchi',
      role: 'React Developer',
      image: 'https://randomuser.me/api/portraits/men/32.jpg'
    }],
    sustainabilityScore: 78,
    sustainabilityMetrics: {
      carbonReduction: 65,
      resourceEfficiency: 82,
      socialImpact: 90
    },
    sustainabilityOpportunities: [{
      id: 1,
      title: 'Optimize product image loading',
      description: 'Implement lazy loading and automatic compression of food product images to reduce bandwidth consumption and server energy.',
      impact: 'high',
      difficulty: 'low',
      potentialImprovement: 8
    }, {
      id: 2,
      title: 'Add CO2 savings calculator',
      description: 'Show users how much CO2 they have saved by avoiding food waste, encouraging sustainable behaviors.',
      impact: 'medium',
      difficulty: 'medium',
      potentialImprovement: 6
    }, {
      id: 3,
      title: 'Implement route optimization algorithm',
      description: 'Create an algorithm that optimizes delivery routes for unsold food to minimize CO2 emissions during transport.',
      impact: 'high',
      difficulty: 'medium',
      potentialImprovement: 12
    }],
    tags: ['Mobile', 'React Native', 'Sustainability', 'Food Tech']
  }, {
    id: 102,
    title: 'E-learning platform for developers',
    description: 'Platform specialized in advanced programming courses and best practices.',
    progress: 40,
    startDate: '05/04/2023',
    endDate: '15/10/2023',
    team: [{
      name: 'Sophia Rossi',
      role: 'UX Designer',
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/woFngADa1aU2Q5MqNXmffj/pexels-olly-774909.jpg'
    }, {
      name: 'Andrea Russo',
      role: 'Full-stack Developer',
      image: 'https://randomuser.me/api/portraits/men/67.jpg'
    }],
    sustainabilityScore: 35,
    sustainabilityMetrics: {
      carbonReduction: 20,
      resourceEfficiency: 40,
      socialImpact: 45
    },
    sustainabilityOpportunities: [{
      id: 1,
      title: 'Optimize educational video streaming',
      description: 'Implement advanced video compression techniques and adaptive quality to reduce bandwidth consumption during course viewing.',
      impact: 'high',
      difficulty: 'medium',
      potentialImprovement: 15
    }, {
      id: 2,
      title: 'Create a sustainable programming module',
      description: 'Develop a specific course on best practices for writing energy-efficient, low-consumption code.',
      impact: 'medium',
      difficulty: 'low',
      potentialImprovement: 10
    }, {
      id: 3,
      title: 'Implement offline mode for courses',
      description: 'Allow users to download courses for offline viewing, reducing continuous energy consumption from streaming.',
      impact: 'high',
      difficulty: 'medium',
      potentialImprovement: 12
    }],
    tags: ['Web', 'React', 'Node.js', 'Education']
  }, {
    id: 103,
    title: 'Energy monitoring system for buildings',
    description: 'IoT system that monitors and optimizes energy consumption in commercial and residential buildings.',
    progress: 85,
    startDate: '10/01/2023',
    endDate: '25/07/2023',
    team: [{
      name: 'Sophia Rossi',
      role: 'UX Designer',
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/woFngADa1aU2Q5MqNXmffj/pexels-olly-774909.jpg'
    }, {
      name: 'Paolo Ferrari',
      role: 'IoT Specialist',
      image: 'https://randomuser.me/api/portraits/men/22.jpg'
    }],
    sustainabilityScore: 92,
    sustainabilityMetrics: {
      carbonReduction: 95,
      resourceEfficiency: 90,
      socialImpact: 75
    },
    sustainabilityOpportunities: [{
      id: 1,
      title: 'Implement ML-based predictive algorithms',
      description: 'Integrate machine learning algorithms that predict energy consumption peaks and automatically suggest real-time optimizations.',
      impact: 'very high',
      difficulty: 'high',
      potentialImprovement: 5
    }, {
      id: 2,
      title: 'Add integration with local smart grids',
      description: 'Connect the system to local smart grids to balance energy load and leverage times of greater renewable energy availability.',
      impact: 'high',
      difficulty: 'high',
      potentialImprovement: 4
    }, {
      id: 3,
      title: 'Develop comparative dashboard for users',
      description: 'Create a dashboard that allows users to compare their consumption with similar buildings, encouraging virtuous competition toward sustainability.',
      impact: 'medium',
      difficulty: 'medium',
      potentialImprovement: 3
    }],
    tags: ['IoT', 'Energy', 'Sustainability', 'Data Visualization']
  }];
  // Global sustainability suggestions
  const globalSustainabilityTips = [{
    id: 1,
    title: 'Carbon Neutral Certification',
    description: 'Obtain Carbon Neutral certification for your software projects demonstrating commitment to sustainability.',
    category: 'Certifications',
    icon: <Award className="w-4 h-4 text-green-600" data-id="element-1192" />
  }, {
    id: 2,
    title: 'Implement energy-optimized CI/CD',
    description: 'Configure CI/CD pipelines that optimize computational resource usage, reducing carbon footprint.',
    category: 'Development',
    icon: <Zap className="w-4 h-4 text-yellow-600" data-id="element-1193" />
  }, {
    id: 3,
    title: 'Adopt "Green by Design" principle',
    description: 'Integrate sustainability considerations from the early stages of software design.',
    category: 'Design',
    icon: <Leaf className="w-4 h-4 text-green-600" data-id="element-1194" />
  }];
  // Benchmark sustainable projects
  const benchmarkProjects = [{
    id: 201,
    title: 'EcoDelivery: Zero-emission logistics',
    description: 'Platform that optimizes urban deliveries using exclusively electric vehicles and bicycles.',
    image: 'https://images.unsplash.com/photo-1527285286036-1ae743926077?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    sustainabilityScore: 96,
    achievement: 'Reduction of 120 tons of CO2 in 6 months',
    company: 'GreenMile Logistics',
    location: 'Milan',
    isWinner: true
  }, {
    id: 202,
    title: 'AquaSave: Water consumption monitoring',
    description: 'IoT system for monitoring and optimizing domestic and industrial water consumption.',
    image: 'https://images.unsplash.com/photo-1581244277943-fe4a9c777189?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    sustainabilityScore: 94,
    achievement: 'Savings of 1.2M liters of water in 3 months',
    company: 'BlueWave Tech',
    location: 'Rome',
    isWinner: false
  }, {
    id: 203,
    title: 'SolarShare: Shared energy',
    description: 'Platform for sharing and distributing solar energy among residential communities.',
    image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    sustainabilityScore: 91,
    achievement: '35% reduction in fossil fuel dependency',
    company: 'SunCommunity',
    location: 'Florence',
    isWinner: false
  }, {
    id: 204,
    title: 'WasteTrack: Smart waste management',
    description: 'App for tracking and optimized management of urban waste.',
    image: 'https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    sustainabilityScore: 89,
    achievement: '45% increase in urban waste recycling',
    company: 'CleanCity Solutions',
    location: 'Turin',
    isWinner: false
  }, {
    id: 205,
    title: 'GreenCode: Sustainable software development',
    description: 'Framework for developing web applications with minimal energy impact.',
    image: 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    sustainabilityScore: 87,
    achievement: '40% reduction in application energy consumption',
    company: 'EcoSoft Labs',
    location: 'Bologna',
    isWinner: false
  }, {
    id: 206,
    title: 'FarmConnect: Precision agriculture',
    description: 'IoT system for optimizing resources in agriculture.',
    image: 'https://images.unsplash.com/photo-1500651230702-0e2d8a49d4ad?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    sustainabilityScore: 86,
    achievement: '50% reduction in pesticide and fertilizer use',
    company: 'AgriTech Solutions',
    location: 'Verona',
    isWinner: false
  }, {
    id: 207,
    title: 'BikeShare: Sustainable urban mobility',
    description: 'Platform for sharing electric bicycles in urban areas.',
    image: 'https://images.unsplash.com/photo-1556316384-12c35d30afa4?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    sustainabilityScore: 85,
    achievement: 'Reduction of 45 tons of CO2 from urban transport',
    company: 'GreenWheels',
    location: 'Naples',
    isWinner: false
  }, {
    id: 208,
    title: 'EcoOffice: Zero-impact offices',
    description: 'Intelligent management system for office buildings.',
    image: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    sustainabilityScore: 83,
    achievement: '35% reduction in energy consumption in offices',
    company: 'SmartWork Environments',
    location: 'Genoa',
    isWinner: false
  }, {
    id: 209,
    title: 'RecycleBot: Recycling automation',
    description: 'Robot for automatic waste separation in collection centers.',
    image: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    sustainabilityScore: 82,
    achievement: '60% increase in waste separation accuracy',
    company: 'AutoRecycle Systems',
    location: 'Palermo',
    isWinner: false
  }, {
    id: 210,
    title: 'GreenHost: Sustainable web hosting',
    description: 'Hosting service powered 100% by renewable energy.',
    image: 'https://images.unsplash.com/photo-1573164713988-8665fc963095?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    sustainabilityScore: 81,
    achievement: 'Zero CO2 emissions for hosting services',
    company: 'EcoCloud Services',
    location: 'Bari',
    isWinner: false
  }];
  const openOpportunityModal = project => {
    setSelectedProject(project);
    setShowOpportunityModal(true);
  };
  const closeOpportunityModal = () => {
    setShowOpportunityModal(false);
    setTimeout(() => {
      setSelectedProject(null);
    }, 300);
  };
  return <div className="flex flex-col min-h-screen w-full bg-white text-black font-sans" data-id="element-1195">
      {/* Main content with white background */}
      <section className="flex-grow bg-white pt-6 pb-20 md:pb-10" data-id="element-1196">
        <div className="max-w-6xl mx-auto px-0" data-id="element-1197">
          {/* Header */}
          <div className="mb-8" data-id="element-1198">
            <h1 className="text-2xl md:text-3xl font-light font-sharp-grotesk mb-2" data-id="element-1199">
              Impact
            </h1>
            <p className="text-gray-600 font-rubik" data-id="element-1200">
              Measure and improve the environmental impact of your projects
            </p>
          </div>
          {/* Navigation tabs */}
          <div className="border-b border-gray-200 mb-6" data-id="element-1201">
            <div className="flex -mb-px" data-id="element-1202">
              <button className={`mr-6 py-2 px-1 text-base font-medium font-rubik border-b-2 ${activeTab === 'projects' ? 'border-black text-black' : 'border-transparent text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('projects')} data-id="element-1203">
                Your Projects
              </button>
              <button className={`mr-6 py-2 px-1 text-base font-medium font-rubik border-b-2 ${activeTab === 'benchmark' ? 'border-black text-black' : 'border-transparent text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('benchmark')} data-id="element-1204">
                Top Ten
              </button>
            </div>
          </div>
          {/* Your Projects Section */}
          {activeTab === 'projects' && <div className="space-y-4" data-id="element-1205">
              {userProjects.map(project => <div key={project.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-1206">
                  <div className="flex flex-col border-b border-gray-100 px-4 py-3" data-id="element-1207">
                    <h3 className="font-medium text-base font-sharp-grotesk" data-id="element-1208">
                      {project.title}
                    </h3>
                    <div className="flex items-center mt-1" data-id="element-1209">
                      <div className="bg-black text-white rounded-full px-2 py-0.5 flex items-center text-xs" data-id="element-1210">
                        <Leaf className="w-3 h-3 mr-1" data-id="element-1211" />
                        <span className="font-medium" data-id="element-1212">
                          {project.sustainabilityScore}%
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="p-4" data-id="element-1213">
                    <div className="mb-4" data-id="element-1214">
                      <p className="text-sm text-gray-600 mb-3 font-rubik" data-id="element-1215">
                        {project.description}
                      </p>
                      <div className="mb-3" data-id="element-1216">
                        <div className="flex items-center justify-between text-xs text-gray-500 mb-1" data-id="element-1217">
                          <div className="flex items-center" data-id="element-1218">
                            <Leaf className="w-3.5 h-3.5 mr-1 text-green-500" data-id="element-1219" />
                            Sustainability
                          </div>
                          <span data-id="element-1220">{project.sustainabilityScore}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-1221">
                          <div className="h-2 rounded-full bg-black" style={{
                      width: `${project.sustainabilityScore}%`
                    }} data-id="element-1222"></div>
                        </div>
                      </div>
                      <div className="flex justify-end" data-id="element-1223">
                        <button onClick={() => openOpportunityModal(project)} className="text-xs font-medium text-gray-600 hover:text-black transition-colors" data-id="element-1224">
                          View details
                        </button>
                      </div>
                    </div>
                    <div className="border-t border-gray-100 pt-3" data-id="element-1225">
                      <div className="flex items-center justify-between" data-id="element-1226">
                        <div className="flex -space-x-2" data-id="element-1227">
                          {project.team.map((member, index) => <div key={index} className="relative group" data-id="element-1228">
                              <img src={member.image} alt={member.name} className="w-7 h-7 rounded-full border-2 border-white object-cover" title={`${member.name} - ${member.role}`} data-id="element-1229" />
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity bg-black text-white text-xs rounded py-1 px-2 whitespace-nowrap pointer-events-none" data-id="element-1230">
                                {member.name}
                              </div>
                            </div>)}
                        </div>
                        <div className="flex gap-2" data-id="element-1231">
                          <button onClick={() => openOpportunityModal(project)} className="text-xs font-medium text-black bg-gray-50 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors border border-gray-100" data-id="element-1232">
                            Suggestions
                          </button>
                          <button onClick={() => navigate('/collaboration')} className="text-xs font-medium text-white bg-black px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-gray-800 transition-colors" data-id="element-1233">
                            Details
                            <ChevronRight className="w-3.5 h-3.5" data-id="element-1234" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>}
          {/* Suggestions Section */}
          {activeTab === 'suggerimenti' && <div className="space-y-6" data-id="element-1235">
              <div className="bg-green-50 p-4 rounded-lg border border-green-100" data-id="element-1236">
                <div className="flex items-start gap-3" data-id="element-1237">
                  <AlertCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" data-id="element-1238" />
                  <div data-id="element-1239">
                    <p className="text-green-800 mb-2 font-medium font-sharp-grotesk" data-id="element-1240">
                      Why sustainability matters
                    </p>
                    <p className="text-sm text-green-700" data-id="element-1241">
                      The IT sector is responsible for about 2-3% of global CO2
                      emissions. Implementing sustainable development practices
                      can significantly reduce the environmental impact of your
                      projects.
                    </p>
                  </div>
                </div>
              </div>
              <h2 className="text-xl font-sharp-grotesk mt-6 mb-4" data-id="element-1242">
                Suggestions for all projects
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1243">
                {globalSustainabilityTips.map(tip => <div key={tip.id} className="border border-gray-100 rounded-lg p-4 shadow-sm" data-id="element-1244">
                    <div className="flex gap-3" data-id="element-1245">
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-1246">
                        {tip.icon}
                      </div>
                      <div data-id="element-1247">
                        <div className="flex items-center mb-1" data-id="element-1248">
                          <h3 className="font-medium font-sharp-grotesk" data-id="element-1249">
                            {tip.title}
                          </h3>
                          <span className="text-xs text-gray-500 ml-2 px-2 py-0.5 bg-gray-100 rounded-full" data-id="element-1250">
                            {tip.category}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600" data-id="element-1251">
                          {tip.description}
                        </p>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>}
          {/* Benchmark Section */}
          {activeTab === 'benchmark' && <div className="space-y-6" data-id="element-1252">
              <div className="flex items-center justify-between mb-4" data-id="element-1253">
                <div className="flex items-center bg-gray-50 rounded-full px-3 py-1 border border-gray-100" data-id="element-1254">
                  <Calendar className="w-4 h-4 text-gray-600 mr-1" data-id="element-1255" />
                  <span className="text-xs font-medium text-gray-800 font-rubik" data-id="element-1256">
                    May 2023
                  </span>
                </div>
              </div>
              {/* Winner project of the month */}
              {benchmarkProjects.filter(project => project.isWinner).map(project => <div key={project.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white" data-id="element-1257">
                    <div className="bg-gray-50 py-2 px-4 flex items-center border-b border-gray-100" data-id="element-1258">
                      <Trophy className="w-5 h-5 text-black mr-2" data-id="element-1259" />
                      <span className="font-medium text-gray-800 font-rubik" data-id="element-1260">
                        Project of the Month
                      </span>
                    </div>
                    <div className="flex flex-col md:flex-row" data-id="element-1261">
                      <div className="md:w-1/3 p-4 flex flex-col justify-center items-center bg-gray-50 border-r border-gray-100" data-id="element-1262">
                        <div className="mb-3" data-id="element-1263">
                          <span className="text-sm font-medium text-gray-700" data-id="element-1264">
                            Project team
                          </span>
                        </div>
                        <div className="flex flex-wrap justify-center gap-2 mb-2" data-id="element-1265">
                          <div className="relative" data-id="element-1266">
                            <div className="w-12 h-12 rounded-full bg-white border-2 border-gray-200 overflow-hidden" data-id="element-1267">
                              <img src="https://randomuser.me/api/portraits/women/44.jpg" alt="Team member" className="w-full h-full object-cover" data-id="element-1268" />
                            </div>
                          </div>
                          <div className="relative" data-id="element-1269">
                            <div className="w-12 h-12 rounded-full bg-white border-2 border-gray-200 overflow-hidden" data-id="element-1270">
                              <img src="https://randomuser.me/api/portraits/men/34.jpg" alt="Team member" className="w-full h-full object-cover" data-id="element-1271" />
                            </div>
                          </div>
                          <div className="relative" data-id="element-1272">
                            <div className="w-12 h-12 rounded-full bg-white border-2 border-gray-200 overflow-hidden" data-id="element-1273">
                              <img src="https://randomuser.me/api/portraits/women/68.jpg" alt="Team member" className="w-full h-full object-cover" data-id="element-1274" />
                            </div>
                          </div>
                          <div className="relative" data-id="element-1275">
                            <div className="w-12 h-12 rounded-full bg-white border-2 border-gray-200 overflow-hidden" data-id="element-1276">
                              <img src="https://randomuser.me/api/portraits/men/45.jpg" alt="Team member" className="w-full h-full object-cover" data-id="element-1277" />
                            </div>
                          </div>
                        </div>
                        <div className="bg-black text-white rounded-full px-2 py-1 flex items-center text-xs mt-2" data-id="element-1278">
                          <Leaf className="w-3 h-3 mr-1" data-id="element-1279" />
                          <span className="font-medium" data-id="element-1280">
                            {project.sustainabilityScore}%
                          </span>
                        </div>
                      </div>
                      <div className="p-4 md:w-2/3" data-id="element-1281">
                        <div className="flex items-center mb-2" data-id="element-1282">
                          <div className="flex items-center" data-id="element-1283">
                            <h3 className="font-medium font-space-grotesk text-base" data-id="element-1284">
                              {project.title}
                            </h3>
                          </div>
                          <div className="ml-auto flex" data-id="element-1285">
                            <span className="bg-black text-white rounded-full px-2 py-0.5 flex items-center text-xs mr-2" data-id="element-1286">
                              <Award className="w-3 h-3 mr-1" data-id="element-1287" />
                              <span className="font-medium" data-id="element-1288">Winner</span>
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 mb-2 font-rubik" data-id="element-1289">
                          <span className="font-medium" data-id="element-1290">{project.company}</span>{' '}
                          • {project.location}
                        </p>
                        <p className="text-sm text-gray-600 mb-3 font-rubik" data-id="element-1291">
                          {project.description}
                        </p>
                        <div className="bg-gray-50 px-3 py-2 rounded-lg flex items-center mb-3 border border-gray-100" data-id="element-1292">
                          <CheckCircle className="w-4 h-4 text-black mr-2" data-id="element-1293" />
                          <span className="text-gray-800 text-sm font-rubik" data-id="element-1294">
                            {project.achievement}
                          </span>
                        </div>
                        <button className="text-sm font-medium text-white bg-black px-4 py-1.5 rounded-lg font-rubik" data-id="element-1295">
                          Learn more
                        </button>
                      </div>
                    </div>
                  </div>)}
              {/* List of other projects */}
              <div className="grid grid-cols-1 gap-4 mt-6" data-id="element-1296">
                {benchmarkProjects.filter(project => !project.isWinner).map((project, index) => <div key={project.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-1297">
                      <div className="flex flex-col sm:flex-row" data-id="element-1298">
                        <div className="sm:w-1/4 p-3 flex flex-col justify-center items-center bg-gray-50 border-r border-gray-100" data-id="element-1299">
                          <div className="flex -space-x-2 mb-2" data-id="element-1300">
                            <div className="relative" data-id="element-1301">
                              <div className="w-8 h-8 rounded-full bg-white border-2 border-gray-200 overflow-hidden" data-id="element-1302">
                                <img src={`https://randomuser.me/api/portraits/${index % 2 === 0 ? 'women' : 'men'}/${30 + index}.jpg`} alt="Team member" className="w-full h-full object-cover" data-id="element-1303" />
                              </div>
                            </div>
                            <div className="relative" data-id="element-1304">
                              <div className="w-8 h-8 rounded-full bg-white border-2 border-gray-200 overflow-hidden" data-id="element-1305">
                                <img src={`https://randomuser.me/api/portraits/${index % 2 === 1 ? 'women' : 'men'}/${40 + index}.jpg`} alt="Team member" className="w-full h-full object-cover" data-id="element-1306" />
                              </div>
                            </div>
                            <div className="relative" data-id="element-1307">
                              <div className="w-8 h-8 rounded-full bg-white border-2 border-gray-200 overflow-hidden" data-id="element-1308">
                                <img src={`https://randomuser.me/api/portraits/${index % 2 === 0 ? 'men' : 'women'}/${50 + index}.jpg`} alt="Team member" className="w-full h-full object-cover" data-id="element-1309" />
                              </div>
                            </div>
                          </div>
                          <div className="bg-black text-white px-2 py-0.5 rounded-full text-xs font-medium mt-1" data-id="element-1310">
                            #{index + 2}
                          </div>
                        </div>
                        <div className="p-4 sm:w-3/4" data-id="element-1311">
                          <div className="flex items-center justify-between mb-1" data-id="element-1312">
                            <h3 className="font-medium font-space-grotesk" data-id="element-1313">
                              {project.title}
                            </h3>
                            <div className="bg-black text-white rounded-full px-2 py-0.5 flex items-center text-xs" data-id="element-1314">
                              <Leaf className="w-3 h-3 mr-1" data-id="element-1315" />
                              <span className="font-medium" data-id="element-1316">
                                {project.sustainabilityScore}%
                              </span>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mb-2 font-rubik" data-id="element-1317">
                            {project.company} • {project.location}
                          </p>
                          <p className="text-sm text-gray-600 mb-3 font-rubik line-clamp-2" data-id="element-1318">
                            {project.description}
                          </p>
                          <div className="flex justify-between items-center" data-id="element-1319">
                            <span className="text-xs text-gray-700 bg-gray-50 px-2 py-1 rounded-full border border-gray-100 font-rubik" data-id="element-1320">
                              {project.achievement}
                            </span>
                            <button className="text-xs font-medium text-black font-rubik" data-id="element-1321">
                              Details
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>)}
              </div>
            </div>}
        </div>
      </section>
      {/* Mobile bottom navigation */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex justify-around py-3 z-50" data-id="element-1322">
        <a href="/" className="flex flex-col items-center" data-id="element-1323">
          <Home className="w-5 h-5 text-gray-400" data-id="element-1324" />
          <span className="text-xs mt-1 text-gray-400 font-rubik" data-id="element-1325">Home</span>
        </a>
        <a href="/connections" className="flex flex-col items-center" data-id="element-1326">
          <Users className="w-5 h-5 text-gray-400" data-id="element-1327" />
          <span className="text-xs mt-1 text-gray-400 font-rubik" data-id="element-1328">
            Connections
          </span>
        </a>
        <a href="/networking" className="flex flex-col items-center" data-id="element-1329">
          <Globe className="w-5 h-5 text-gray-400" data-id="element-1330" />
          <span className="text-xs mt-1 text-gray-400 font-rubik" data-id="element-1331">
            Networking
          </span>
        </a>
        <a href="/collaboration" className="flex flex-col items-center" data-id="element-1332">
          <Briefcase className="w-5 h-5 text-gray-400" data-id="element-1333" />
          <span className="text-xs mt-1 text-gray-400 font-rubik" data-id="element-1334">
            Collaboration
          </span>
        </a>
        <a href="/impact" className="flex flex-col items-center" data-id="element-1335">
          <Leaf className="w-5 h-5 text-black" data-id="element-1336" />
          <span className="text-xs mt-1 font-rubik" data-id="element-1337">Impact</span>
        </a>
      </div>
      {/* Opportunity Modal */}
      {showOpportunityModal && selectedProject && <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center" data-id="element-1338">
          <div className="fixed inset-0 bg-black/30 transition-opacity" onClick={closeOpportunityModal} data-id="element-1339"></div>
          <div className="bg-white w-full rounded-t-xl md:rounded-xl overflow-hidden transform transition-all duration-300 ease-out animate-slide-up max-h-[90vh] relative z-10" data-id="element-1340">
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-2rem)]" data-id="element-1341">
              <div className="flex items-center justify-between mb-5" data-id="element-1342">
                <h3 className="text-xl font-sharp-grotesk" data-id="element-1343">
                  Sustainability details
                </h3>
                <button onClick={closeOpportunityModal} className="text-gray-500 hover:text-black transition-colors" data-id="element-1344">
                  <X className="w-5 h-5" data-id="element-1345" />
                </button>
              </div>
              <div className="space-y-6" data-id="element-1346">
                {/* Header with title and score */}
                <div className="flex flex-col space-y-2" data-id="element-1347">
                  <h4 className="font-medium font-sharp-grotesk text-lg" data-id="element-1348">
                    {selectedProject.title}
                  </h4>
                  <div className="flex items-center" data-id="element-1349">
                    <div className="bg-black text-white rounded-full px-3 py-1 flex items-center" data-id="element-1350">
                      <Leaf className="w-4 h-4 mr-1.5" data-id="element-1351" />
                      <span className="font-medium" data-id="element-1352">
                        {selectedProject.sustainabilityScore}%
                      </span>
                    </div>
                    <span className="text-sm text-gray-600 ml-2" data-id="element-1353">
                      sustainability score
                    </span>
                  </div>
                </div>
                {/* Score distribution */}
                <div className="bg-white border border-gray-200 p-5 rounded-lg" data-id="element-1354">
                  <h5 className="text-base font-medium mb-4 font-sharp-grotesk" data-id="element-1355">
                    Score distribution
                  </h5>
                  <div className="space-y-4" data-id="element-1356">
                    <div data-id="element-1357">
                      <div className="flex justify-between items-center mb-2" data-id="element-1358">
                        <span className="text-sm font-medium" data-id="element-1359">
                          CO₂ Reduction
                        </span>
                        <span className="font-sharp-grotesk" data-id="element-1360">
                          {selectedProject.sustainabilityMetrics.carbonReduction}
                          %
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2" data-id="element-1361">
                        <div className="bg-black h-2 rounded-full" style={{
                      width: `${selectedProject.sustainabilityMetrics.carbonReduction}%`
                    }} data-id="element-1362"></div>
                      </div>
                    </div>
                    <div data-id="element-1363">
                      <div className="flex justify-between items-center mb-2" data-id="element-1364">
                        <span className="text-sm font-medium" data-id="element-1365">
                          Resource efficiency
                        </span>
                        <span className="font-sharp-grotesk" data-id="element-1366">
                          {selectedProject.sustainabilityMetrics.resourceEfficiency}
                          %
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2" data-id="element-1367">
                        <div className="bg-black h-2 rounded-full" style={{
                      width: `${selectedProject.sustainabilityMetrics.resourceEfficiency}%`
                    }} data-id="element-1368"></div>
                      </div>
                    </div>
                    <div data-id="element-1369">
                      <div className="flex justify-between items-center mb-2" data-id="element-1370">
                        <span className="text-sm font-medium" data-id="element-1371">
                          Social impact
                        </span>
                        <span className="font-sharp-grotesk" data-id="element-1372">
                          {selectedProject.sustainabilityMetrics.socialImpact}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2" data-id="element-1373">
                        <div className="bg-black h-2 rounded-full" style={{
                      width: `${selectedProject.sustainabilityMetrics.socialImpact}%`
                    }} data-id="element-1374"></div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Improvement opportunities */}
                <div data-id="element-1375">
                  <div className="flex items-center justify-between mb-4" data-id="element-1376">
                    <h5 className="text-base font-medium font-sharp-grotesk" data-id="element-1377">
                      Improvement opportunities
                    </h5>
                    <span className="text-xs text-gray-500" data-id="element-1378">
                      Implement these suggestions to improve your impact
                    </span>
                  </div>
                  <div className="space-y-4" data-id="element-1379">
                    {selectedProject.sustainabilityOpportunities.map(opportunity => <div key={opportunity.id} className="bg-white border border-gray-200 p-4 rounded-lg" data-id="element-1380">
                          <div className="flex justify-between items-start mb-3" data-id="element-1381">
                            <h6 className="font-medium text-base" data-id="element-1382">
                              {opportunity.title}
                            </h6>
                            <span className="bg-black text-white px-2 py-0.5 rounded-full text-xs font-medium ml-2 whitespace-nowrap" data-id="element-1383">
                              +{opportunity.potentialImprovement}%
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-4" data-id="element-1384">
                            {opportunity.description}
                          </p>
                          <div className="flex justify-between items-center" data-id="element-1385">
                            <div className="flex gap-3" data-id="element-1386">
                              <div className="flex items-center" data-id="element-1387">
                                <span className="w-2 h-2 rounded-full mr-1.5 
                                  ${opportunity.difficulty === 'low' ? 'bg-green-500' : 
                                    opportunity.difficulty === 'medium' ? 'bg-yellow-500' : 'bg-red-500'}" data-id="element-1388"></span>
                                <span className="text-xs text-gray-600" data-id="element-1389">
                                  {opportunity.difficulty}
                                </span>
                              </div>
                              <div className="flex items-center" data-id="element-1390">
                                <span className="w-2 h-2 rounded-full mr-1.5 
                                  ${opportunity.impact === 'high' || opportunity.impact === 'very high' ? 
                                    'bg-blue-500' : 'bg-purple-500'}" data-id="element-1391"></span>
                                <span className="text-xs text-gray-600" data-id="element-1392">
                                  {opportunity.impact} impact
                                </span>
                              </div>
                            </div>
                            <button className="text-xs bg-black text-white px-3 py-1.5 rounded-lg hover:bg-gray-800 transition-colors" data-id="element-1393">
                              Implement
                            </button>
                          </div>
                        </div>)}
                  </div>
                </div>
              </div>
              <button onClick={closeOpportunityModal} className="w-full border border-gray-200 text-black py-2 rounded-lg hover:bg-gray-50 transition-colors mt-8 font-medium" data-id="element-1394">
                Close
              </button>
            </div>
          </div>
        </div>}
    </div>;
}
export default {
  ImpactPage
};